import lodash from 'lodash'
import store from '@/store'
import { getSameBuildingUsersByPermission } from '@/api/users/request'
import { DesignBatchStatus, tenancyTypeMap, tenantProjectTypes } from '@/utils/locale'
import { displayI18nFullName } from '@/utils/fullName'
import { aclHelper, tmsPermsTypes } from '@/utils/aclHelper'
import { GREEN_FITOUT_STATUS } from '@/modules/tenantProjects/greenFitOut/consts'
import i18n from '../../utils/i18n'

export function getProjectName (project, tenancy) {
  try {
    const companyName = lodash.get(tenancy, 'company.name', i18n.t('emptyValue'))
    const tenancyType = tenancyTypeMap[tenancy.type] ? i18n.t(`tenancy.${tenancyTypeMap[tenancy.type]}`) : i18n.t('emptyValue')
    const projectType = lodash.get(project, 'type')
    let projectLocation = lodash.get(project, 'projectLocation', i18n.t('emptyValue'))
    projectLocation = Array.isArray(projectLocation) ? projectLocation.join(', ') : projectLocation
    const building = lodash.get(tenancy, 'building.fullName', i18n.t('emptyValue'))
    const type = projectType ? i18n.t(`tenancyProject.${lodash.toLower(projectType)}`) : i18n.t('emptyValue')
    const projectName = i18n.t(`tenancyProject.projectName`, [ companyName, type, building, tenancyType, projectLocation ])
    return projectName
  } catch (err) {
    console.error(`[getProjectName] ${err}`)
    return project.projectName
  }
}

export function getTabIndexByName (tabName) {
  let tenantTabName = ['projectInfo', 'submissions', 'projectControl']
  const workPermitsEnabled = store.getters['regions/enableWorkPermitsFeature']

  if (workPermitsEnabled) {
    tenantTabName.splice(2, 0, 'contractorInfo', 'workPermit')
  }
  const index = tenantTabName.findIndex(name => name === tabName)
  return index > -1 ? index : 0
}

export function getBatchDocName (doc) {
  let suffix = deep(doc, 'metaData.isNa') ? ` (${i18n.t('excluded')})` : ''
  return `${doc.filename}${suffix}`
}

export function replaceDisplayName (displayName, accountId, accounts) {
  const account = accounts.find(account => account.id === accountId)
  const getAccount = lodash.get(account, 'TenantProjectAccount') || lodash.get(account, 'TenancyAccount')
  if (getAccount) {
    displayName = displayI18nFullName(getAccount.contactFirstName, getAccount.contactLastName)
  }
  return displayName
}

export async function getUsersForSameBuilding (tenancy, permType, currentReviewer, { includeTrReps = false } = {}) {
  const users = await getSameBuildingUsersByPermission({
    buildingId: tenancy.building.id,
    tenancyId: includeTrReps ? tenancy.id : void 0,
    tenancyType: tenancy.type,
    includeSelf: false,
    permTypes: permType,
  })
  if (currentReviewer && users) {
    lodash.remove(users, user => user.id === currentReviewer.id)
  }
  return users.reduce((partialData, user) => {
    const name = user.name
    const curUser = {
      displayName: user.displayName || name,
      displayPicName: `${name} (${user.username})`,
      id: user.id,
      email: user.email,
      username: user.username,
    }
    return partialData.concat(curUser)
  }, [])
}

export function doDefaultSort (files) {
  const formatFiles = lodash.cloneDeep(files).map(item => {
    if (item.batchNum) {
      item.batchNum = Number(item.batchNum)
    }
    return item
  })
  let sortFuncs = ['submitted', 'isNa', 'submittedDate'].map(item => (obj) => (obj.metaData[item] || ''))
  sortFuncs.unshift('batchNum')
  const order = ['desc', 'asc', 'asc', 'desc']
  return lodash.orderBy(formatFiles, sortFuncs, order)
}

export function isLetterReviewer (underReviewLetter) {
  const currentUser = store.getters['user/currentUser']
  return underReviewLetter && underReviewLetter.managedBy === currentUser.id
}
export function showDocForSubmission (tenantProject) {
  return lodash.findIndex(tenantProject.projectDocs, doc =>
    doc.batchNum === null && lodash.get(doc, 'metaData.submitted', false) && !lodash.get(doc, 'metaData.isNa', false),
  ) !== -1
}
export function getSubmissionSubmittedDesignComment (submission) {
  return lodash.get(submission, 'submittedDesignComment.comment')
}
export function isSubmissionCPDisapprove (submission) {
  return getSubmissionSubmittedDesignComment(submission) === DesignBatchStatus.DISAPPROVE
}
export function isSubmissionCPNoFollowUp (submission) {
  return getSubmissionSubmittedDesignComment(submission) === DesignBatchStatus.PROCEED_NO_FOLLOW_UP
}

/**
 * Whether technical review has started for a submission batch.
 * @param {import('@/store/modules/tenantProject').Submission | null | undefined} submission
 * @returns {boolean}
 */
export function hasTechnicalReviewStarted (submission) {
  return Boolean(lodash.get(submission, 'techComment.startedAt'))
}

/**
 * Whether design or technical review has started on a submission batch.
 * @param {import('@/store/modules/tenantProject').Submission | null | undefined} submission
 * @returns {boolean}
 */
export function hasSubmissionReviewStarted (submission) {
  return Boolean(lodash.get(submission, 'designComment.startedAt')) ||
    hasTechnicalReviewStarted(submission)
}

/**
 * Pending submission with design or tech review already in progress.
 * @param {import('@/store/modules/tenantProject').Submission | null | undefined} submission
 * @returns {boolean}
 */
export function isPendingSubmissionUnderReview (submission) {
  return Boolean(submission && !submission.completedDate && hasSubmissionReviewStarted(submission))
}

/**
 * Tenant-facing batches: completed batches, or pending batches after technical review starts.
 * Design-only review does not expose batch labels to external users.
 * @param {import('@/store/modules/tenantProject').Submission | null | undefined} submission
 * @returns {boolean}
 */
export function isTenantFacingBatchVisible (submission) {
  if (!submission) {
    return false
  }
  if (submission.completedDate) {
    return true
  }
  return hasTechnicalReviewStarted(submission) ||
    Boolean(lodash.get(submission, 'techManagedBy'))
}

/**
 * @param {import('@/store/modules/tenantProject').Submission[]} [submissions]
 * @returns {import('@/store/modules/tenantProject').Submission[]}
 */
export function filterTenantFacingSubmissions (submissions = []) {
  return (submissions || []).filter(isTenantFacingBatchVisible)
}

/**
 * Latest submission visible to external (tenant) users.
 * @param {{ submissions?: import('@/store/modules/tenantProject').Submission[] }} rec
 * @returns {import('@/store/modules/tenantProject').Submission | undefined}
 */
export function getLastTenantFacingSubmission (rec) {
  return lodash.orderBy(filterTenantFacingSubmissions(rec.submissions), 'order', 'desc')[0]
}

/**
 * Latest submission whose batch label is visible to external users.
 * @param {{ submissions?: import('@/store/modules/tenantProject').Submission[] }} rec
 * @returns {import('@/store/modules/tenantProject').Submission | undefined}
 */
export function getLastExternalUserVisibleSubmission (rec) {
  return getLastTenantFacingSubmission(rec)
}

/**
 * @param {import('@/store/modules/tenantProject').Submission[]} submissions
 * @param {number | string | null | undefined} batchNum
 * @returns {boolean}
 */
export function isTenantFacingBatchNumVisible (submissions, batchNum) {
  if (batchNum === null || batchNum === undefined || batchNum === '') {
    return false
  }
  const submission = (submissions || []).find(item => Number(item.order) === Number(batchNum))
  return isTenantFacingBatchVisible(submission)
}

/**
 * External users do not see batch labels until the technical reviewer starts review.
 * Completed batches remain visible.
 * @param {import('@/store/modules/tenantProject').Submission[]} submissions
 * @param {number | string | null | undefined} batchNum
 * @returns {boolean}
 */
export function isExternalUserBatchLabelVisible (submissions, batchNum) {
  return isTenantFacingBatchNumVisible(submissions, batchNum)
}

/**
 * Submissions whose batch labels are visible to external users.
 * @param {import('@/store/modules/tenantProject').Submission[]} [submissions]
 * @returns {import('@/store/modules/tenantProject').Submission[]}
 */
export function filterExternalUserVisibleSubmissions (submissions = []) {
  return filterTenantFacingSubmissions(submissions)
}

export function showDocForGreenFitout (greenFitout) {
  const canReview = aclHelper.getAccess(
    tmsPermsTypes.project.reviewGreenFitout,
  )
  return lodash.get(greenFitout, 'newDocsList', []).length && canReview
}

export function isShowPinForGreenFitout (greenFitout) {
  const currentUser = store.getters['user/currentUser']
  const canReview = aclHelper.getAccess(
    tmsPermsTypes.project.reviewGreenFitout,
  )
  const canCommentOn = aclHelper.getAccess(
    tmsPermsTypes.project.commentOnGreenFitout,
  )
  // HKL user clicked on hold
  if (greenFitout.onHold || greenFitout.status === GREEN_FITOUT_STATUS.COMPLETED) {
    return false
  }

  if (greenFitout.status === GREEN_FITOUT_STATUS.UNDER_REVIEW) {
    return greenFitout.managedBy === currentUser.id
  }
  // Has folder
  if (lodash.get(greenFitout, 'folders', []).length) {
    return canReview || canCommentOn
  }
}

export function isShowPinForSubmissionsComments ({ showSubmissionPin, showLetterPin, showGreenFitoutPin } = {}) {
  return Boolean(showSubmissionPin || showLetterPin || showGreenFitoutPin)
}

export function isShowPinForSubmission (tenantProject, submission) {
  const canCommentDesign = aclHelper.getAccess(
    tmsPermsTypes.project.commentDesign,
  )
  const canCommentTech = aclHelper.getAccess(tmsPermsTypes.project.commentTech)
  const canSend = aclHelper.getAccess(tmsPermsTypes.project.send)

  const lastSubmission = submission || lodash.orderBy(tenantProject.submissions || [], 'order', 'desc').filter(submission => !!submission)[0]
  const currentUser = store.getters['user/currentUser']

  const isNewBatchStart = lastSubmission && !lastSubmission.completedDate
  if (!isNewBatchStart && showDocForSubmission(tenantProject)) {
    const hasDesign = lodash.some(tenantProject.projectDocs, { batchNum: null, metaData: { isDesign: true } })
    const hasTechnical = lodash.some(tenantProject.projectDocs, { batchNum: null, metaData: { isTechnical: true } })
    return (hasTechnical && canCommentTech) ||
          (hasDesign && canCommentDesign) ||
          (hasDesign && canCommentTech) // for TM & TSS, show Pin even there's design docs only
  }

  if (isNewBatchStart) {
    const batchDocs = tenantProject.projectDocs.filter(doc => doc.batchNum !== null && Number(doc.batchNum) === Number(lastSubmission.order))

    const hasDesign = lodash.findIndex(batchDocs, doc =>
      doc.metaData && doc.metaData.isDesign === true,
    ) !== -1
    const hasTech = lodash.findIndex(batchDocs, doc =>
      doc.metaData && doc.metaData.isTechnical === true,
    ) !== -1
    // Should show pin if reinstatement has under review submission but no docs add to this batch
    const reinsStartButNoDoc = tenantProject.type === tenantProjectTypes.Reinstatement && !batchDocs.length

    // If all docs are excluded, not show pin,
    if (reinsStartButNoDoc || hasDesign || hasTech) {
      // canStartReview
      //    there's no tech/design comment, and current user has the permission to comment tech/design
      //    there's a hasDesign restraint for CP, hide Pin for CP if there's no design docs
      const canStartReviewDesign = canCommentDesign && !lastSubmission.designManagedBy && hasDesign
      const canStartReviewTech = canCommentTech && !lastSubmission.techManagedBy
      const canStartReview = canStartReviewDesign || canStartReviewTech

      // isCurrentReviewer
      //    there's tech/design comment, and current user commented(manages) the tech/design and has the permission
      const isCurrentTechReviewer = canCommentTech && currentUser.id === lastSubmission.techManagedBy
      const isCurrentDesignReviewer = canCommentDesign && currentUser.id === lastSubmission.designManagedBy
      const isCurrentReviewer = isCurrentTechReviewer || isCurrentDesignReviewer

      const canStartReviewOrReviewedTech = canStartReviewTech || isCurrentTechReviewer
      const canStartReviewOrReviewed = canStartReview || isCurrentReviewer

      // CP won't follow up anymore, only show pin for tech engineer(TSS) & tech manager(TM)
      if (isSubmissionCPNoFollowUp(lastSubmission)) return canStartReviewOrReviewedTech

      // CP disapproved
      //    if tech started review, show pin for currentTechReviewer
      //    if tech didn't start review, show pin for tech manager(TM)
      if (isSubmissionCPDisapprove(lastSubmission)) {
        return lastSubmission.techManagedBy
          ? isCurrentTechReviewer
          : (canSend && canCommentTech)
      }

      // CP didn't disapprove / didn't choose no follow up, show pin for CP(if there's design docs) / TSS / TM
      return canStartReviewOrReviewed
    }
  }

  return false
}

/**
 *
 * @param {string[]} emails
 * @param {string} emailSeparator
 */
export const concatUserEmails = (emails, emailSeparator) => {
  const uniqEmails = lodash.uniq(emails)
  return uniqEmails.reduce((result, email) => {
    const prefix = result === '' ? '' : emailSeparator
    return result.concat(prefix).concat(email)
  }, '')
}

/**
 * Region env may return a non-string (e.g. `{}`) when the request fails; value must be a non-empty string.
 * @param {unknown} value
 * @returns {boolean}
 */
export function hasAccountingEmail (value) {
  return typeof value === 'string' && value.trim().length > 0
}

/**
 * @template {Record<string, string>} T
 * @template {Record<string, string>} AT
 * @param {T} types
 * @param {AT} actionTypes
 */
export function genBuildFitOutHistoryFn (types, actionTypes) {
  /**
   * @param {keyof T} type
   * @param {keyof AT} actionType
   * @param {object?} metaData
   */
  return function (type, actionType, metaData) {
    if (!Object.values(types).includes(type)) {
      throw new Error(`Invalid history type: ${Object.values(types)}`)
    }
    if (!Object.values(actionTypes).includes(actionType)) {
      throw new Error(`Invalid history action type: ${Object.values(actionTypes)}`)
    }
    return Object.assign({}, metaData, {
      type,
      actionType,
      createdAt: new Date(),
    })
  }
}

/** Whether tenant-project HKL primary link should be “View Tenancy Record” (PMS-262). */
export function showViewTenancyRecordLink (isMainUser, isSubUser) {
  return !(isMainUser || isSubUser)
}
